import { Download, HelpCircle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';

interface InstallPromptProps {
  onOpenHelp: () => void;
}

export const InstallPrompt: React.FC<InstallPromptProps & { isWelcomeModalOpen: boolean }> = ({ onOpenHelp, isWelcomeModalOpen }) => {
  const { isInstalled, deferredPrompt, promptInstall, isMobile } = usePWAInstall();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user has dismissed the prompt before
    const hasDismissed = localStorage.getItem('pwa_prompt_dismissed');
    

    // Show if not installed, not dismissed, and either we have a prompt (Android/Desktop) 
    // or we just want to show it on iOS (where deferredPrompt is null)
    // AND Welcome Modal is NOT open
    // AND is Mobile
    if (!isInstalled && !hasDismissed && !isWelcomeModalOpen && isMobile) {
      // Small delay to not be intrusive immediately
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
        setIsVisible(false);
    }
  }, [isInstalled, deferredPrompt, isWelcomeModalOpen, isMobile]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  const handleInstall = async () => {
    try {
        setIsLoading(true);
        if (deferredPrompt) {
          const outcome = await promptInstall();
          if (outcome === 'failed') {
             // Fallback if prompt fails
             setIsLoading(false);
             onOpenHelp();
          } else {
             // Success or user dismissed the system dialog
             // We can close our prompt
             setIsLoading(false);
             setIsVisible(false);
          }
        } else {
          // If no prompt available (e.g. iOS), open help immediately
          setIsLoading(false);
          onOpenHelp();
        }
    } catch (error) {
        console.error("Install failed:", error);
        setIsLoading(false);
        onOpenHelp();
    }
  };

  if (!isVisible) return null;

  // Minimized Loading State
  if (isLoading) {
      return (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-40 animate-in fade-in zoom-in duration-300">
            <div className="bg-brand-dark text-white px-4 py-2 rounded-full shadow-xl flex items-center gap-3">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="text-xs font-bold">Installing...</span>
            </div>
        </div>
      );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 animate-in slide-in-from-bottom-10 duration-500">
      <div className="bg-brand-dark text-white p-4 rounded-xl shadow-2xl flex items-center justify-between gap-4 max-w-md mx-auto">
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2 bg-white/10 rounded-lg">
            <Download className="w-6 h-6 text-brand-cream" />
          </div>
          <div>
            <p className="font-bold text-sm">Install App</p>
            <p className="text-xs text-gray-300">Add to home screen for better experience</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={handleInstall}
            className="px-3 py-1.5 bg-brand-red text-white text-xs font-bold rounded-lg shadow-sm active:scale-95 transition-transform"
          >
            Install
          </button>
          
          <button
            onClick={onOpenHelp}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Help"
          >
            <HelpCircle className="w-5 h-5 text-gray-400" />
          </button>

          <button 
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};
